// SPV Pool v1.0 PR 5 — typed transport for the Investor surface
// (/api/investors, /api/investor-commitments, /api/investor-distributions).
// Mutation hooks live in @/hooks/capitalMarkets/useInvestors.

import { api } from "./client";
import type { PagedResult } from "@/types/api";
import type {
  InvestorCommitmentCreate,
  InvestorCommitmentDto,
  InvestorCommitmentListQuery,
  InvestorCreate,
  InvestorDistributionDto,
  InvestorDistributionListQuery,
  InvestorListQuery,
  InvestorMaskedDto,
  InvestorSensitiveDto,
  InvestorUpdate,
} from "@/types/investors";

export const investorsApi = {
  list: (query?: InvestorListQuery) =>
    api.get<PagedResult<InvestorMaskedDto>>("/investors", query),
  get: (id: string) => api.get<InvestorMaskedDto>(`/investors/${id}`),
  /**
   * Reveals plaintext tax ID + banking instructions. The backend writes a
   * sensitive-reveal audit-log entry on every call; the response is never
   * cached or persisted by the caller.
   */
  getSensitive: (id: string) =>
    api.get<InvestorSensitiveDto>(`/investors/${id}/sensitive`),
  create: (body: InvestorCreate) =>
    api.post<InvestorMaskedDto>("/investors", body),
  update: (id: string, body: InvestorUpdate) =>
    api.put<InvestorMaskedDto>(`/investors/${id}`, body),
  delete: (id: string) => api.delete<void>(`/investors/${id}`),
};

export const investorCommitmentsApi = {
  list: (query?: InvestorCommitmentListQuery) =>
    api.get<PagedResult<InvestorCommitmentDto>>("/investor-commitments", query),
  get: (id: string) =>
    api.get<InvestorCommitmentDto>(`/investor-commitments/${id}`),
  create: (body: InvestorCommitmentCreate) =>
    api.post<InvestorCommitmentDto>("/investor-commitments", body),
  delete: (id: string) => api.delete<void>(`/investor-commitments/${id}`),
};

export const investorDistributionsApi = {
  list: (query?: InvestorDistributionListQuery) =>
    api.get<PagedResult<InvestorDistributionDto>>(
      "/investor-distributions",
      query,
    ),
};
